import { useState } from 'react'
import { motion } from 'framer-motion'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import {
    Users, LayoutGrid, List, ChevronRight,
    TrendingUp, TrendingDown, Minus
} from 'lucide-react'
import { mockMentorStudents, type MentorStudent, getStudentsNeedingAttention, getBatches } from '@/data/mentor-mock-data'

type ViewMode = 'cards' | 'table'
type FilterPreset = 'all' | 'needs-attention' | 'at-risk' | 'all-good'

interface AssignedStudentsViewProps {
    onViewStudent?: (studentId: string) => void
}

export function AssignedStudentsView({ onViewStudent }: AssignedStudentsViewProps = {}) {
    const [viewMode, setViewMode] = useState<ViewMode>('cards')
    const [filterPreset, setFilterPreset] = useState<FilterPreset>('all')
    const [selectedBatch, setSelectedBatch] = useState<string>('all')
    const [selectedCourse, setSelectedCourse] = useState<string>('all')
    const [searchQuery, setSearchQuery] = useState('')
    const [selectedStudents, setSelectedStudents] = useState<string[]>([])

    // Get unique courses from students
    const uniqueCourses = Array.from(new Set(mockMentorStudents.map(s => s.courseName).filter(Boolean))) as string[]

    // Filter students based on active filters
    const filteredStudents = mockMentorStudents.filter(student => {
        // Search filter
        if (searchQuery && !student.name.toLowerCase().includes(searchQuery.toLowerCase())) {
            return false
        }

        // Batch filter
        if (selectedBatch !== 'all' && student.batch !== selectedBatch) {
            return false
        }

        // Course filter
        if (selectedCourse !== 'all' && student.courseName !== selectedCourse) {
            return false
        }

        // Preset filters
        if (filterPreset === 'needs-attention') {
            return !student.todayHoursSubmitted || student.dropRisk === 'high' ||
                student.currentStreak === 0 || student.performanceTrend.includes('declining')
        }
        if (filterPreset === 'at-risk') {
            return student.dropRisk === 'high' || student.dropRisk === 'medium'
        }
        if (filterPreset === 'all-good') {
            return student.todayHoursSubmitted && student.dropRisk === 'low' &&
                !student.performanceTrend.includes('declining')
        }

        return true
    })

    const batches = getBatches()
    const needsAttentionCount = getStudentsNeedingAttention().length
    const atRiskCount = mockMentorStudents.filter(s => s.dropRisk === 'high' || s.dropRisk === 'medium').length

    const handleSelectStudent = (studentId: string) => {
        if (selectedStudents.includes(studentId)) {
            setSelectedStudents(selectedStudents.filter(id => id !== studentId))
        } else {
            setSelectedStudents([...selectedStudents, studentId])
        }
    }

    const handleSelectAll = () => {
        if (selectedStudents.length === filteredStudents.length) {
            setSelectedStudents([])
        } else {
            setSelectedStudents(filteredStudents.map(s => s.id))
        }
    }

    const getTrendIcon = (trend: MentorStudent['performanceTrend']) => {
        if (trend.includes('improving')) return <TrendingUp className="h-4 w-4 text-green-500" />
        if (trend.includes('declining')) return <TrendingDown className="h-4 w-4 text-red-500" />
        return <Minus className="h-4 w-4 text-slate-400" />
    }

    const getStatusColor = (student: MentorStudent) => {
        if (!student.todayHoursSubmitted || student.dropRisk === 'high' || student.performanceTrend.includes('strongly-declining')) {
            return 'border-l-red-500 bg-red-50/30 dark:bg-red-900/5'
        }
        if (student.dropRisk === 'medium' || student.performanceTrend.includes('declining')) {
            return 'border-l-yellow-500 bg-yellow-50/30 dark:bg-yellow-900/5'
        }
        return 'border-l-green-500 bg-green-50/30 dark:bg-green-900/5'
    }

    const getStatusBadge = (student: MentorStudent) => {
        if (!student.todayHoursSubmitted || student.dropRisk === 'high') {
            return <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs font-bold rounded-full">🔴 At Risk</span>
        }
        if (student.dropRisk === 'medium' || student.performanceTrend.includes('declining')) {
            return <span className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 text-xs font-bold rounded-full">🟡 Needs Review</span>
        }
        return <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-bold rounded-full">🟢 On Track</span>
    }

    return (
        <div className="space-y-4 md:space-y-6 pb-24 lg:pb-0">
            {/* Header with Stats */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h2 className="text-xl md:text-2xl font-bold text-theme-primary">Assigned Students</h2>
                    <p className="text-sm text-theme-secondary mt-1">
                        Showing {filteredStudents.length} students | {needsAttentionCount} need attention | {atRiskCount} at-risk
                    </p>
                </div>

                {/* View Mode Toggle */}
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setViewMode('cards')}
                        className={`p-2 rounded-lg transition-colors ${viewMode === 'cards'
                            ? 'bg-primary-blue text-white'
                            : 'bg-slate-100 dark:bg-slate-800 text-theme-secondary hover:bg-slate-200 dark:hover:bg-slate-700'
                            }`}
                    >
                        <LayoutGrid className="h-5 w-5" />
                    </button>
                    <button
                        onClick={() => setViewMode('table')}
                        className={`p-2 rounded-lg transition-colors ${viewMode === 'table'
                            ? 'bg-primary-blue text-white'
                            : 'bg-slate-100 dark:bg-slate-800 text-theme-secondary hover:bg-slate-200 dark:hover:bg-slate-700'
                            }`}
                    >
                        <List className="h-5 w-5" />
                    </button>
                </div>
            </div>

            {/* Filters */}
            <GlassmorphicCard className="p-4">
                <div className="flex flex-col md:flex-row md:items-center gap-3">
                    {/* Search */}
                    <div className="flex-1">
                        <input
                            type="text"
                            placeholder="Search students..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full px-4 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 border border-transparent focus:border-primary-blue text-theme-primary placeholder:text-theme-secondary transition-all outline-none"
                        />
                    </div>

                    {/* Course Filter */}
                    <select
                        value={selectedCourse}
                        onChange={(e) => setSelectedCourse(e.target.value)}
                        className="px-4 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 border border-transparent focus:border-primary-blue text-theme-primary transition-all outline-none"
                    >
                        <option value="all">All Courses</option>
                        {uniqueCourses.map(course => (
                            <option key={course} value={course}>{course}</option>
                        ))}
                    </select>

                    {/* Batch Filter */}
                    <select
                        value={selectedBatch}
                        onChange={(e) => setSelectedBatch(e.target.value)}
                        className="px-4 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 border border-transparent focus:border-primary-blue text-theme-primary transition-all outline-none"
                    >
                        <option value="all">All Batches</option>
                        {batches.map(batch => (
                            <option key={batch} value={batch}>{batch}</option>
                        ))}
                    </select>

                    {/* Preset Filters */}
                    <div className="flex gap-2 flex-wrap">
                        <button
                            onClick={() => setFilterPreset('all')}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${filterPreset === 'all'
                                ? 'bg-primary-blue text-white shadow-lg shadow-blue-500/25'
                                : 'bg-slate-100 dark:bg-slate-800 text-theme-secondary hover:bg-slate-200 dark:hover:bg-slate-700'
                                }`}
                        >
                            All
                        </button>
                        <button
                            onClick={() => setFilterPreset('needs-attention')}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${filterPreset === 'needs-attention'
                                ? 'bg-amber-500 text-white'
                                : 'bg-slate-100 dark:bg-slate-800 text-theme-secondary hover:bg-slate-200 dark:hover:bg-slate-700'
                                }`}
                        >
                            Needs Attention
                        </button>
                        <button
                            onClick={() => setFilterPreset('at-risk')}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${filterPreset === 'at-risk'
                                ? 'bg-red-500 text-white'
                                : 'bg-slate-100 dark:bg-slate-800 text-theme-secondary hover:bg-slate-200 dark:hover:bg-slate-700'
                                }`}
                        >
                            At Risk
                        </button>
                    </div>
                </div>
            </GlassmorphicCard>

            {/* Bulk Actions Bar (when students selected) */}
            {viewMode === 'table' && selectedStudents.length > 0 && (
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <GlassmorphicCard className="p-4 bg-blue-50 dark:bg-blue-900/10 border-l-4 border-l-primary-blue">
                        <div className="flex items-center justify-between">
                            <p className="text-sm font-semibold text-theme-primary">
                                {selectedStudents.length} student{selectedStudents.length > 1 ? 's' : ''} selected
                            </p>
                            <div className="flex gap-2">
                                <button className="px-4 py-2 bg-green-500 text-white text-sm font-semibold rounded-lg hover:bg-green-600 transition-colors">
                                    Approve All
                                </button>
                                <button className="px-4 py-2 bg-primary-blue text-white text-sm font-semibold rounded-lg hover:bg-blue-600 transition-colors">
                                    Assign Plan
                                </button>
                                <button className="px-4 py-2 bg-slate-500 text-white text-sm font-semibold rounded-lg hover:bg-slate-600 transition-colors">
                                    Send Message
                                </button>
                            </div>
                        </div>
                    </GlassmorphicCard>
                </motion.div>
            )}

            {/* Student Cards View */}
            {viewMode === 'cards' && (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {filteredStudents.map((student) => (
                        <motion.div
                            key={student.id}
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.2 }}
                        >
                            <GlassmorphicCard className={`p-4 border-l-4 ${getStatusColor(student)} hover:shadow-lg transition-all cursor-pointer`}>
                                <div className="space-y-3">
                                    {/* Header with Avatar and Name */}
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-blue to-blue-600 flex items-center justify-center text-white font-bold text-lg">
                                                {student.name.split(' ').map(n => n[0]).join('')}
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-theme-primary">{student.name}</h3>
                                                <p className="text-xs text-theme-secondary">NEET {student.targetYear}</p>
                                            </div>
                                        </div>
                                        {getStatusBadge(student)}
                                    </div>

                                    {/* Quick Stats */}
                                    <div className="grid grid-cols-2 gap-2 text-xs">
                                        <div className="p-2 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                                            <p className="text-theme-secondary">Last Active</p>
                                            <p className="font-semibold text-theme-primary">{student.lastActivity}</p>
                                        </div>
                                        <div className="p-2 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                                            <p className="text-theme-secondary">Today's Hours</p>
                                            <p className="font-semibold text-theme-primary">
                                                {student.todayHoursSubmitted ? (
                                                    <span className="text-green-600">✓ {student.todayHoursReported}h</span>
                                                ) : (
                                                    <span className="text-red-600">❌ Not submitted</span>
                                                )}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Performance & Trend */}
                                    <div className="flex items-center justify-between text-sm">
                                        <div>
                                            <p className="text-xs text-theme-secondary">Avg Score</p>
                                            <p className="font-bold text-theme-primary">{student.avgTestScore}/720</p>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            {getTrendIcon(student.performanceTrend)}
                                            <span className={`font-semibold ${student.trendValue > 0 ? 'text-green-600' : student.trendValue < 0 ? 'text-red-600' : 'text-slate-500'}`}>
                                                {student.trendValue > 0 ? '+' : ''}{student.trendValue}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Weak Subjects */}
                                    {student.weakSubjects.length > 0 && (
                                        <div>
                                            <p className="text-xs text-theme-secondary mb-1">Weak Subjects:</p>
                                            <div className="flex gap-1 flex-wrap">
                                                {student.weakSubjects.map(subject => (
                                                    <span key={subject} className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs rounded-full">
                                                        {subject}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Action Button */}
                                    <button
                                        onClick={() => onViewStudent?.(student.id)}
                                        className="w-full py-2 bg-primary-blue text-white text-sm font-semibold rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
                                    >
                                        Open Profile
                                        <ChevronRight className="h-4 w-4" />
                                    </button>
                                </div>
                            </GlassmorphicCard>
                        </motion.div>
                    ))}
                </div>
            )}

            {/* Table View */}
            {viewMode === 'table' && (
                <GlassmorphicCard className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-slate-200 dark:border-slate-700">
                                <th className="p-3 text-left">
                                    <input
                                        type="checkbox"
                                        checked={selectedStudents.length === filteredStudents.length}
                                        onChange={handleSelectAll}
                                        className="rounded"
                                    />
                                </th>
                                <th className="p-3 text-left text-xs font-semibold text-theme-secondary uppercase">Student</th>
                                <th className="p-3 text-left text-xs font-semibold text-theme-secondary uppercase">Status</th>
                                <th className="p-3 text-left text-xs font-semibold text-theme-secondary uppercase">Today's Hours</th>
                                <th className="p-3 text-left text-xs font-semibold text-theme-secondary uppercase">Last Active</th>
                                <th className="p-3 text-left text-xs font-semibold text-theme-secondary uppercase">Performance</th>
                                <th className="p-3 text-left text-xs font-semibold text-theme-secondary uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredStudents.map((student) => (
                                <tr
                                    key={student.id}
                                    className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                                >
                                    <td className="p-3">
                                        <input
                                            type="checkbox"
                                            checked={selectedStudents.includes(student.id)}
                                            onChange={() => handleSelectStudent(student.id)}
                                            className="rounded"
                                        />
                                    </td>
                                    <td className="p-3">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-blue to-blue-600 flex items-center justify-center text-white font-bold text-sm shrink-0">
                                                {student.name.split(' ').map(n => n[0]).join('')}
                                            </div>
                                            <div>
                                                <p className="font-semibold text-theme-primary text-sm">{student.name}</p>
                                                <p className="text-xs text-theme-secondary">NEET {student.targetYear}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="p-3">{getStatusBadge(student)}</td>
                                    <td className="p-3">
                                        {student.todayHoursSubmitted ? (
                                            <span className="text-sm font-semibold text-green-600">
                                                ✓ {student.todayHoursReported}h
                                            </span>
                                        ) : (
                                            <span className="text-sm font-semibold text-red-600">❌ Pending</span>
                                        )}
                                    </td>
                                    <td className="p-3">
                                        <span className="text-sm text-theme-secondary">{student.lastActivity}</span>
                                    </td>
                                    <td className="p-3">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-semibold text-theme-primary">{student.avgTestScore}</span>
                                            <div className="flex items-center gap-1">
                                                {getTrendIcon(student.performanceTrend)}
                                                <span className={`text-xs font-semibold ${student.trendValue > 0 ? 'text-green-600' : student.trendValue < 0 ? 'text-red-600' : 'text-slate-500'}`}>
                                                    {student.trendValue > 0 ? '+' : ''}{student.trendValue}
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="p-3">
                                        <button
                                            onClick={() => onViewStudent?.(student.id)}
                                            className="px-3 py-1.5 bg-primary-blue text-white text-xs font-semibold rounded-lg hover:bg-blue-600 transition-colors"
                                        >
                                            Details
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </GlassmorphicCard>
            )}

            {/* Empty State */}
            {filteredStudents.length === 0 && (
                <GlassmorphicCard className="p-12 text-center">
                    <Users className="h-16 w-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                    <h3 className="text-lg font-bold text-theme-primary mb-2">No students found</h3>
                    <p className="text-theme-secondary">Try adjusting your filters</p>
                </GlassmorphicCard>
            )}
        </div>
    )
}
